// The carrier sync-attempt vocabulary DC reports (`policy_sync_attempts.status`).
//
// Lives in `shared/` because both sides need it and they must not drift: the
// server polls on it (`server/services/dcSyncAttempts.ts`) and the client labels
// it (the CSR-1993 Stale Sync tab). Distinct from the AMS-normalized
// `PolicySyncOutcome` (`succeeded | no_change | failed | not_found |
// unattributable`), which is what AMS derives for its own surfaces — this is
// DC's raw wire vocabulary.
//
// CSR-1976 added `no_change`: the scrape reached the carrier and finished
// cleanly, but nothing in DC's tracked columns differed. It is a terminal
// SUCCESS, not a failure — DC narrowed `completed` to mean "a real change was
// written", so consumers must handle the two separately.
export type DcSyncAttemptStatus =
  | "waiting"
  | "running"
  | "completed"
  | "no_change"
  | "failed"
  | "not_found"
  | "stopped"
  | "interrupted";

export const ALL_SYNC_ATTEMPT_STATUSES: DcSyncAttemptStatus[] = [
  "waiting",
  "running",
  "completed",
  "no_change",
  "failed",
  "not_found",
  "stopped",
  "interrupted",
];

/** Narrow an untrusted wire value to the known vocabulary, or null. Callers
 *  decide what null means — it is NOT interchangeable with "no attempt". */
export function asDcSyncAttemptStatus(
  value: unknown,
): DcSyncAttemptStatus | null {
  return typeof value === "string" &&
    (ALL_SYNC_ATTEMPT_STATUSES as string[]).includes(value)
    ? (value as DcSyncAttemptStatus)
    : null;
}

// CSR-2174 — why DC has no sync attempt to report for a policy term. Mirrors
// DC's own `shared/sync-attempt.ts` (CSR-2172), which puts this `reason` on the
// 404 body of `GET /api/external/policies/:id/latest-sync-attempt`. The repos
// share no code, so this copy is the contract; keep the members identical.
//
// ⚠️ The polarity is load-bearing, and it is DC's rule, not ours: **a 404 with
// NO reason means "keep polling"**. A DC that predates CSR-2172 sends no reason
// and AMS then behaves exactly as it did before, which is what makes the deploy
// order between the two repos irrelevant. Every reason below therefore names a
// PERMANENT condition — no amount of further polling can change the answer —
// which is also why AMS stops on a reason it does not recognize rather than
// polling on (see `checkPolicySyncAttempt`).
export const SYNC_ATTEMPT_UNAVAILABLE_REASONS = [
  "policy_not_found",
  "not_carrier_synced",
  "superseded_term",
  "ambiguous_term",
] as const;

export type SyncAttemptUnavailableReason =
  (typeof SYNC_ATTEMPT_UNAVAILABLE_REASONS)[number];

/** Narrow a reason DC sent to the vocabulary this build knows, or null.
 *  `null` here means "a reason arrived that we do not recognize" and is NOT
 *  the same as no reason at all — only the caller can see that difference, so
 *  it must keep the raw value alongside this. */
export function asSyncAttemptUnavailableReason(
  value: unknown,
): SyncAttemptUnavailableReason | null {
  return typeof value === "string" &&
    (SYNC_ATTEMPT_UNAVAILABLE_REASONS as readonly string[]).includes(value)
    ? (value as SyncAttemptUnavailableReason)
    : null;
}

/** What a CSR is told when a Refresh cannot be attributed to the term they are
 *  looking at. Deliberately distinct from DC's own developer-facing error
 *  strings: these are the sentences a person reads after clicking Refresh.
 *
 *  A `Record` rather than a `switch`, so adding a member to the enum above
 *  fails to compile until someone writes the words a human will read — the
 *  whole point of CSR-2171 was that a missing distinction became a confident
 *  lie ("Sync did not complete within 8 minutes") on ~45% of the GEICO book.
 *
 *  Two things none of these may say, and the second is easy to get wrong:
 *
 *  1. None may claim the refresh FAILED. That is the lie being deleted.
 *  2. **None may claim the carrier data WAS refreshed either.** All four
 *     reasons are decided from the term's own shape, before DC looks at
 *     `policy_sync_attempts` at all — and the scrape AMS just triggered is
 *     asynchronous, so at the first poll (~3s in) it has almost certainly not
 *     finished. "Carrier data refreshed" would therefore be a second confident
 *     falsehood replacing the first, only faster. What is actually known is
 *     narrow and permanent: no attempt can ever be attributed to THIS term.
 *     Say exactly that. (DC's own CSR-2172 messages carry the same rule.)
 */
export const SYNC_ATTEMPT_UNAVAILABLE_CSR_MESSAGES: Record<
  SyncAttemptUnavailableReason,
  string
> = {
  policy_not_found:
    "This policy is no longer in Data Center, so a carrier update cannot be confirmed for it.",
  not_carrier_synced:
    "This policy is not synced from a carrier portal, so there is nothing to refresh.",
  superseded_term:
    "This term was replaced by a renewal. Refresh the current term to see carrier updates.",
  ambiguous_term:
    "More than one live term shares this policy number, so a carrier update cannot be confirmed for this one.",
};

/** Shown when DC sends a reason newer than this build knows. It still stops the
 *  poll (every reason is permanent by DC's contract) but must not invent a
 *  cause it cannot name — nor claim an outcome it cannot observe. */
export const SYNC_ATTEMPT_UNAVAILABLE_FALLBACK_MESSAGE =
  "Data Center cannot confirm a carrier update for this specific term.";

/** The sentence a CSR sees, for a known reason or an unrecognized one. */
export function syncAttemptUnavailableMessage(
  reason: SyncAttemptUnavailableReason | null,
): string {
  return reason === null
    ? SYNC_ATTEMPT_UNAVAILABLE_FALLBACK_MESSAGE
    : SYNC_ATTEMPT_UNAVAILABLE_CSR_MESSAGES[reason];
}
